import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { TRADEFINDER_TABS, type TradeFinderTab, type TradeFinderTabProps } from './tabs';

interface TradeFinderPanelProps extends TradeFinderTabProps {
  onClose: () => void;
  mobile?: boolean;
  /** Sheet handle / header touch handlers (mobile swipe-to-close). */
  dragHandlers?: React.HTMLAttributes<HTMLDivElement>;
  /** Defaults to the registry; tests pass their own. */
  tabs?: TradeFinderTab[];
}

/** Which sides of a sideways-scrolling strip hide more tabs. */
export function tabStripOverflow(el: Pick<HTMLElement, 'scrollLeft' | 'clientWidth' | 'scrollWidth'>): { left: boolean; right: boolean } {
  return {
    left: el.scrollLeft > 1,
    right: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
  };
}

/** Title bar, tab strip from the registry, the active tab's body. */
const TradeFinderPanel: React.FC<TradeFinderPanelProps> = ({
  onClose, mobile, dragHandlers, tabs = TRADEFINDER_TABS, ...tabProps
}) => {
  const [active, setActive] = useState(tabs[0]?.id);
  const tab = tabs.find((t) => t.id === active) ?? tabs[0];
  const stripRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [more, setMore] = useState({ left: false, right: false });

  // Same strip as the Levels panel: one row that scrolls sideways when the
  // labels do not fit (never wraps or squeezes them into each other), with
  // the hidden side faded.
  const measure = () => {
    const el = stripRef.current;
    if (!el) return;
    const next = tabStripOverflow(el);
    setMore((p) => (p.left === next.left && p.right === next.right ? p : next));
  };
  useEffect(() => {
    measure();
    const el = stripRef.current;
    const ro = typeof ResizeObserver !== 'undefined' && el ? new ResizeObserver(measure) : null;
    if (ro && el) ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Keep the active tab in view in the sideways-scrolling strip.
  useEffect(() => {
    if (tab) tabRefs.current[tab.id]?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [tab]);

  return (
    <div className="levels-panel tradefinder-panel-body">
      <div className="levels-panel-top">
        <div className="levels-sheet-drag" {...(mobile ? dragHandlers : {})}>
          {mobile && <div className="levels-sheet-handle" aria-hidden="true" />}
          <div className="levels-dropdown-header">
            <span className="levels-title">TradeFinder</span>
            <div className="levels-header-actions">
              <button type="button" className="levels-icon-btn levels-close-btn" onClick={onClose} aria-label="Close" title="Close">
                <X size={15} />
              </button>
            </div>
          </div>
        </div>
        <div
          ref={stripRef}
          className={`levels-tabs tradefinder-tabs ${more.left ? 'more-left' : ''} ${more.right ? 'more-right' : ''}`}
          role="tablist"
          aria-label="TradeFinder"
          onScroll={measure}
          data-testid="tradefinder-tabs"
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              ref={(el) => { tabRefs.current[t.id] = el; }}
              type="button"
              role="tab"
              aria-selected={t.id === tab?.id}
              className={`levels-tab ${t.id === tab?.id ? 'is-active' : ''}`}
              onClick={() => setActive(t.id)}
              data-testid={`tradefinder-tab-${t.id}`}
            >
              <span className="levels-tab-label">{t.label}</span>
              {t.Badge && <t.Badge />}
            </button>
          ))}
        </div>
      </div>
      <div className="levels-list" role="tabpanel" aria-label={tab?.label} data-testid="tradefinder-tabpanel">
        {tab && <tab.Component {...tabProps} />}
      </div>
    </div>
  );
};

export default TradeFinderPanel;
